import { SphereDropdownSelector } from "@components/common/dropdown/sphere-dropdown-selector";
import { ExportAllDataDialog } from "@components/common/export/export-all-data-dialog";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useLoadingState } from "@context-providers/loading-state-context";
import { TextVariant, Skeleton, useToast } from "@flat-ui";
import { useAppDispatch } from "@store/store-helper";
import { setBulkActionName } from "@store/table/table-slice";
import { ExportType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState, useRef, useCallback } from "react";
import { BulkExportButton } from "@components/common/export/bulk-export-button";
import { SphereDropdownButton } from "@components/common/dropdown/sphere-dropdown-button";

/** Label to display for each available export format */
const EXPORT_FORMAT_LABEL = {
  CSV: ".csv",
  XLSX: ".xlsx (Excel)",
  JSON: ".json"
};
/** Dropdown to export data in the file format selected by the user */
export function ExportDropdown({
  trackingProps,
  exportHandler,
  toastMessage,
  buttonTooltipText = "Export",
  shouldShowSkeleton,
  buttonType = "bulk",
  shouldShowExportAllDialog = false
}) {
  const {
    openToast
  } = useToast();
  const dispatch = useAppDispatch();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const loadingStateContext = useLoadingState();
  const [isExportAllDialogOpen, setIsExportAllDialogOpen] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(0);
  const [currentFormat, setCurrentFormat] = useState(null);
  const abortControllerRef = useRef(null);

  // Use explicit prop if provided, otherwise fall back to context, otherwise default to false
  const isLoading = shouldShowSkeleton ?? loadingStateContext?.isLoading ?? false;

  /** Handles cancel action from the export all progress dialog */
  const handleCancel = useCallback(() => {
    if (currentFormat) {
      trackEvent({
        name: ExportType.exportTableCancelled,
        props: {
          ...trackingProps,
          format: currentFormat,
          exportType: "all"
        }
      });
    }
    abortControllerRef.current?.abort();
    setIsExportAllDialogOpen(false);
    setCurrentFormat(null);
    setCurrentPageNumber(0);
  }, [currentFormat, trackEvent, trackingProps]);

  /**
   * Export handler that supports both selected and all export types.
   * @param format Format to export the data as (CSV, XLSX, JSON)
   */
  const handleExport = useCallback(async format => {
    const startTime = performance.now();
    const amplitudeProps = {
      ...trackingProps,
      format,
      exportType: trackingProps.areEntitiesSelected ? "selected" : "all",
      areEntitiesSelected: trackingProps.areEntitiesSelected
    };

    // Track export started event
    trackEvent({
      name: ExportType.exportTableStarted,
      props: amplitudeProps
    });

    // Set "Export All" dialog state if needed
    if (shouldShowExportAllDialog) {
      setCurrentFormat(format);
      setCurrentPageNumber(0);
      setIsExportAllDialogOpen(true);
    }
    setBulkActionName("export");

    // Create abort controller for this export
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    try {
      // Build options for export handler
      const options = shouldShowExportAllDialog ? {
        onProgress: currentPage => {
          setCurrentPageNumber(currentPage);
        },
        signal: abortController.signal
      } : undefined;
      const result = await exportHandler(format, options);

      // If cancelled, don't show success toast
      if (result === null) {
        return;
      }

      // Track export success event
      trackEvent({
        name: ExportType.exportTable,
        props: {
          ...amplitudeProps,
          numberOfEntities: result,
          // Include time in milliseconds it took to complete the export operation
          elapsedTimeInMilliseconds: performance.now() - startTime
        }
      });
      openToast({
        title: toastMessage.success
      });
    } catch (error) {
      const errorId = `handleExport-${format}-${Date.now()}`;
      trackEvent({
        name: ExportType.exportTableFailed,
        props: {
          ...amplitudeProps,
          errorMessage: toastMessage.error,
          errorId
        }
      });
      handleErrorWithToast({
        id: errorId,
        title: toastMessage.error,
        error
      });
    } finally {
      abortControllerRef.current = null;
      if (shouldShowExportAllDialog) {
        setIsExportAllDialogOpen(false);
        setCurrentFormat(null);
        setCurrentPageNumber(0);
      } else {
        dispatch(setBulkActionName(null));
      }
    }
  }, [trackingProps, trackEvent, shouldShowExportAllDialog, exportHandler, openToast, toastMessage, handleErrorWithToast, dispatch]);
  if (isLoading) {
    return <Skeleton variant="rectangular" sx={{
      width: "80px",
      height: "24px"
    }} />;
  }
  return <>
      <SphereDropdownSelector items={[{
      value: "CSV",
      label: EXPORT_FORMAT_LABEL.CSV
    }, {
      value: "XLSX",
      label: EXPORT_FORMAT_LABEL.XLSX
    }, {
      value: "JSON",
      label: EXPORT_FORMAT_LABEL.JSON
    }]} defaultLabel="Export" handleChange={handleExport} menuPosition={buttonType === "bulk" ? {
      anchorOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "bottom",
        horizontal: "left"
      }
    } : undefined} renderButton={props => buttonType === "bulk" ? <BulkExportButton {...props} buttonTooltipText={buttonTooltipText} /> : <SphereDropdownButton {...props} buttonTooltipText={buttonTooltipText} iconColor="primary.main" variant={TextVariant.buttonS} />} />

      <ExportAllDataDialog isOpen={isExportAllDialogOpen} currentPageNumber={currentPageNumber} onCancel={handleCancel} />
    </>;
}